// Tracking values we inherited from Logomaker
export const PAGE_NAME = {
  DESIGN_REVIEW: 'DESIGN REVIEW',
  SHAREABLE_LOGO: 'SHAREABLE LOGO',
  BRAND_SHOP: 'Brand Shop',
  BRAND_KIT: 'Brand Kit',
  BRAND_BUILDER: 'Brand Builder',
}

const PAGE_SECTION = {
  DESIGN_SERVICES: 'Design Services',
  BRAND_SHOP: 'My Account',
}

const PAGE_STAGE = {
  LOGOMAKER: 'Logomaker',
  BRAND_SHOP: 'My Account',
}

const ROUTE = 'logomaker'
const NAME = `Logo Maker`

export enum LOGOMAKER_VERSION {
  COMPOSER = 'Classic',
  GENAI = 'GenAI',
  ICON_SOURCE = 'GenAI Logomaker',
  NON_LOGOMAKER = 'Non Logomaker',
}

// Logomaker is also a product itself
const LOGOMAKER_CORE_PRODUCT_ID = 'PRD-WRFGWR7PP'
const LOGOMAKER_CORE_PRODUCT_VERSION = 0
const LOGOMAKER_MPV_ID = 'logoMaker'

export type PageContext = {
  pageUrl: string
  locale: string | undefined
  source: string
  utmSource: string
  utmId: string
  pageName: string
}

type AnalyticsProperties = {
  utmSource: string
  utmId: string
  pageSection: string
  pageStage: string
  pageName: string
  host: string
}

const getPageProperties = (context: PageContext): AnalyticsProperties => {
  return {
    host: context.source,
    utmSource: context.utmSource,
    utmId: context.utmId,
    pageSection: PAGE_SECTION.BRAND_SHOP,
    pageStage: PAGE_STAGE.BRAND_SHOP,
    pageName: `${PAGE_SECTION.BRAND_SHOP}:${context.pageName}`,
  }
}

export type ProductSpecificEventProps = {
  productName: string
  productVersion: string
  prd: string
  mpvId: string
}

const getProductProperties = (product?: ProductSpecificEventProps) => {
  if (product) {
    return {
      product_id: product.mpvId,
      name: product.productName,
      core_product_id: product.prd,
      core_product_version: product.productVersion,
    }
  }

  return {
    product_id: LOGOMAKER_MPV_ID,
    route: ROUTE,
    name: NAME,
    core_product_id: LOGOMAKER_CORE_PRODUCT_ID,
    core_product_version: LOGOMAKER_CORE_PRODUCT_VERSION,
  }
}

export type PageViewedProps = {
  brandType?: string
}

export const getPageEvent = (props: PageViewedProps, pageContext: PageContext) => {
  const langAndLocale = pageContext.locale?.split('-') || []
  return {
    pageName: '', // maintaining behaviour from Logomaker - looking at various vista pages this value is often empty
    payload: {
      language: langAndLocale[0],
      locale: langAndLocale[1],
      tenant: 'vistaprint',
      ...getPageProperties(pageContext),
    },
  }
}

export type ButtonClickedProps = {
  label?: string
  eventDetail: string
  variant?: string
  product?: ProductSpecificEventProps
  category?: string
}

export const getButtonClickedEvent = ({ label, eventDetail, variant, product, category }: ButtonClickedProps, pageContext: PageContext) => {
  return {
    eventName: 'Button Clicked',
    payload: {
      label: label || eventDetail,
      category: category || 'Review',
      eventDetail: eventDetail,
      ...(pageContext.pageName === PAGE_NAME.BRAND_BUILDER && { pageDept: 'Brand Kit' }),
      ...(variant && { variant: variant }),
      ...getProductProperties(product),
      ...getPageProperties(pageContext),
    },
  }
}

export type LogoVariantsAddedProps = {
  label: string
  eventDetail?: string
  category?: string
}

export const getTrackLogoVariantsAdded = ({ label, eventDetail, category }: LogoVariantsAddedProps, pageContext: PageContext) => {
  return {
    eventName: 'Logo Variants Added',
    payload: {
      category,
      label,
      eventDetail,
      ...(pageContext.pageName === PAGE_NAME.BRAND_BUILDER && { pageDept: 'Brand Kit' }),
      ...getPageProperties(pageContext),
    },
  }
}

export type NavigationClickedProps = {
  label: string
  navigationDetail: string
  eventDetail?: string
  category?: string
}

export const getNavigationClickedEvent = (
  { label, navigationDetail, eventDetail, category }: NavigationClickedProps,
  pageContext: PageContext,
) => {
  return {
    eventName: 'Navigation Clicked',
    payload: {
      category: category || 'Review',
      label: label,
      eventDetail: eventDetail,
      navigationDetail: navigationDetail,
      ...(pageContext.pageName === PAGE_NAME.BRAND_BUILDER && { pageDept: 'Brand Kit' }),
      ...getProductProperties(),
      ...getPageProperties(pageContext),
    },
  }
}

export type PromotionProps = {
  label?: string
  navigationDetail?: string
  promotion_id?: string
  creative?: string
  name?: string
  creative_slot?: string
  eventDetail?: string
  category?: string
}

export const getPromotionClickedEvent = (
  { label, navigationDetail, promotion_id, creative, name, creative_slot, eventDetail, category }: PromotionProps,
  pageContext: PageContext,
) => {
  return {
    eventName: 'Promotion Clicked',
    payload: {
      category: category || 'Review',
      label: label,
      promotion_id: promotion_id,
      creative: creative,
      name: name,
      creative_slot: creative_slot,
      eventDetail: eventDetail,
      navigationDetail: navigationDetail,
      ...(pageContext.pageName === PAGE_NAME.BRAND_BUILDER && { pageDept: 'Brand Kit' }),
      ...getPageProperties(pageContext),
    },
  }
}

export const getPromotionViewedEvent = (
  { label, navigationDetail, promotion_id, creative, name, creative_slot, eventDetail, category }: PromotionProps,
  pageContext: PageContext,
) => {
  return {
    eventName: 'Promotion Viewed',
    payload: {
      category: category || 'Review',
      label: label,
      promotion_id: promotion_id,
      creative: creative,
      name: name,
      creative_slot: creative_slot,
      eventDetail: eventDetail,
      navigationDetail: navigationDetail,
      ...(pageContext.pageName === PAGE_NAME.BRAND_BUILDER && { pageDept: 'Brand Kit' }),
      ...getPageProperties(pageContext),
    },
  }
}

export const getExperimentClickedEvent = (
  { label, navigationDetail, promotion_id, creative, name, creative_slot, eventDetail, category }: PromotionProps,
  pageContext: PageContext,
) => {
  return {
    eventName: 'Experiment Clicked',
    payload: {
      category: category || 'Review',
      label: label,
      promotion_id: promotion_id,
      creative: creative,
      name: name,
      creative_slot: creative_slot,
      eventDetail: eventDetail,
      navigationDetail: navigationDetail,
      ...(pageContext.pageName === PAGE_NAME.BRAND_BUILDER && { pageDept: 'Brand Kit' }),
      ...getPageProperties(pageContext),
    },
  }
}

export type ProductViewedProps = {
  logoId: string
  product?: ProductSpecificEventProps
}

export const getProductViewedEvent = ({ logoId, product }: ProductViewedProps, pageContext: PageContext) => {
  return {
    eventName: 'Product Viewed',
    payload: {
      label: 'Review page view',
      variant: logoId ? `logoId:${logoId}` : '',
      ...getProductProperties(product),
      ...getPageProperties(pageContext),
    },
  }
}

export type TabSelectedProps = {
  tabName: string
}

export const getTabSelectedEvent = ({ tabName }: TabSelectedProps, pageContext: PageContext) => {
  return {
    eventName: 'Tab Selected',
    payload: {
      category: 'Digital Marketing',
      label: tabName,
      pageDept: 'Digital Marketing - LogoMaker',
      ...getProductProperties(),
      ...getPageProperties(pageContext),
    },
  }
}

export type LogomakerCrosssellComponentProps = {
  productLoaded: string
}

export const getLogomakerCrosssellComponentEvent = ({ productLoaded }: LogomakerCrosssellComponentProps, pageContext: PageContext) => {
  return {
    eventName: 'Logomaker crosssell component',
    payload: {
      label: 'Download Page Recommendation:' + productLoaded,
      ...getProductProperties(),
      ...getPageProperties(pageContext),
    },
  }
}

export type DownloadPageLogoDetailsProps = {
  companyNameLength?: string
  taglineLength?: string
  companyNameFont?: string
  taglineFont?: string
  iconType: string
  colorType?: string
  flipIcon?: string
  layout?: string
  container?: string
  logoId: string
  logoSelectionFrom?: string
  totalFavLogos?: number
  industry?: string
  industryKeywords?: string
  iconId?: string
  sourceName: string
  totalGradientInFav?: string
  isPremium?: boolean
  galleryLogoIndex?: number
  isEngagedWithFontFilter?: boolean
  royaltyTier?: string
  iconCreatedBy?: string
  iconUrl?: string
  versionGroupId?: string
}

export const getDownloadPageLogoDetailsEvent = (props: DownloadPageLogoDetailsProps, pageContext: PageContext) => {
  return {
    eventName: 'DownloadPage Logo Details',
    payload: {
      label: 'DownloadPage:Logo Details',
      ...getProductProperties(),
      ...getPageProperties(pageContext),
      companyNameLength: props.companyNameLength,
      taglineLength: props.taglineLength,
      companyNameFont: props.companyNameFont,
      taglineFont: props.taglineFont,
      iconType: props.iconType,
      flipIcon: props.flipIcon,
      colorType: props.colorType,
      layout: props.layout,
      container: props.container,
      logoId: props.logoId,
      logoSelectionFrom: props.logoSelectionFrom,
      totalFavLogos: props.totalFavLogos,
      totalGradientInFav: props.totalGradientInFav,
      industry: props.industry,
      industryKeywords: props.industryKeywords,
      iconId: props.iconId,
      iconSourceName: props.sourceName,
      isPremium: props.isPremium,
      galleryLogoIndex: props.galleryLogoIndex,
      isEngagedWithFontFilter: props.isEngagedWithFontFilter || false,
      royaltyTier: props.royaltyTier,
      iconCreatedBy: props?.iconCreatedBy,
      iconUrl: props?.iconUrl,
      versionGroupId: props.versionGroupId,
    },
  }
}

export type LogoDownloadedProps = {
  logoId: string
  logomakerVersion: string
}

export const getLogoDownloadedEvent = (props: LogoDownloadedProps, pageContext: PageContext) => {
  return {
    eventName: 'Logo Downloaded',
    payload: {
      label: 'Logo Downloaded',
      ...getProductProperties(),
      ...getPageProperties(pageContext),
      logoId: props.logoId,
      logomakerVersion: props.logomakerVersion,
    },
  }
}

export type EmailLogoDataGeneratedProps = {
  shopperId: string
  emailColoredLogoUrl: string
  emailTransparentLogoUrl: string
  recommendationUploadUrl: string
  downloadPageLogoUrl?: string
  brandId: string
  businessName: string
}

export const getEmailLogoDataGeneratedEvent = (props: EmailLogoDataGeneratedProps, context: PageContext) => {
  return {
    eventName: 'Email logo data generated',
    payload: {
      label: 'DownloadPage: Email Logo Details',
      ...getProductProperties(),
      ...getPageProperties(context),
      user_shopper_id: props.shopperId,
      email_colored_logo_url: props.emailColoredLogoUrl,
      email_transparent_logo_url: props.emailTransparentLogoUrl,
      recommendations_transparent_logo_url: props.recommendationUploadUrl,
      download_logo_page_url: context.pageUrl,
      brand_id: props.brandId,
      business_name: props.businessName,
    },
  }
}

export type FlyOutViewedEventProps = ProductSpecificEventProps

export const getFlyOutViewedEvent = (props: FlyOutViewedEventProps, pageContext: PageContext) => {
  return {
    eventName: 'Fly-Out Viewed',
    payload: {
      category: 'Fly-Out',
      label: 'Logomaker Download Fly-Out',
      ...getPageProperties(pageContext),
      ...getProductProperties(props),
    },
  }
}

export type FlyOutClosedEventProps = ProductSpecificEventProps

export const getFlyOutClosedEvent = (props: FlyOutClosedEventProps, pageContext: PageContext) => {
  return {
    eventName: 'Fly-Out Closed',
    payload: {
      category: 'Fly-Out',
      label: 'Logomaker Download Fly-Out',
      ...getPageProperties(pageContext),
      ...getProductProperties(props),
    },
  }
}

export type FlyOutClickedPros = {
  optionType: string
  optionSelected: string
} & ProductSpecificEventProps

export const getFlyOutClickedEvent = (props: FlyOutClickedPros, pageContext: PageContext) => {
  return {
    eventName: 'Fly-Out Clicked',
    payload: {
      category: 'Fly-Out',
      label: 'Logomaker Download Product Builder',
      eventDetail: props.optionType,
      variant: props.optionSelected,
      ...getPageProperties(pageContext),
      ...getProductProperties(props),
    },
  }
}

export type ButtonClickedEditInStudioProps = {
  variant: string
} & ProductSpecificEventProps

export const getButtonClickedEditInStudioEvent = (props: ButtonClickedEditInStudioProps, pageContext: PageContext) => {
  return {
    eventName: 'Button Clicked',
    payload: {
      label: 'Edit in studio',
      category: 'Review',
      variant: props.variant,
      ...getPageProperties(pageContext),
      ...getProductProperties(props),
    },
  }
}

export const trackingConfigurationForBrandShop = {
  pageSection: PAGE_SECTION.BRAND_SHOP,
  pageStage: PAGE_STAGE.BRAND_SHOP,
  pageName: `${PAGE_SECTION.BRAND_SHOP}:${PAGE_NAME.BRAND_SHOP}`,
}

export const trackingConfigurationForDownloadPage = {
  pageSection: PAGE_SECTION.DESIGN_SERVICES,
  pageStage: PAGE_STAGE.LOGOMAKER,
  pageName: `${PAGE_NAME.DESIGN_REVIEW}:${PAGE_SECTION.DESIGN_SERVICES}`,
}
